import React, { useState } from 'react'
import { Form } from 'react-router-dom'
import InfoBox from '../components/InfoBox'
import PageLayout from '../components/PageLayout'

interface QuestionPageProps {
  buttons: Array<{
    text: React.ReactNode | string
    subtext?: string
    value: string
  }>
  heading: React.ReactNode | string
  subheading?: React.ReactNode | string
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void
  withLogin?: boolean
  headingSize?: string
  buttonTextSize?: string
  buttonBgColor?: string
  infoText?: string
  preselectedValue?: string
}

export default function QuestionPage({
  withLogin = false,
  heading,
  subheading,
  buttons,
  onSubmit,
  headingSize = 'text-xl',
  buttonTextSize = 'text-base',
  buttonBgColor = 'bg-transparent',
  infoText,
  preselectedValue,
}: QuestionPageProps) {
  const [clickedValue, setClickedValue] = useState<string | null>(
    preselectedValue || null,
  )

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (onSubmit) {
      // Inject the clicked value into the form manually if needed
      const hiddenInput = document.createElement('input')
      hiddenInput.type = 'hidden'
      hiddenInput.name = 'answer'
      hiddenInput.value = clickedValue || ''
      event.currentTarget.appendChild(hiddenInput)

      onSubmit(event)

      // Clean up the hidden input
      event.currentTarget.removeChild(hiddenInput)
    }
  }

  return (
    <PageLayout withLogin={withLogin}>
      <div className="flex grow flex-col">
        <div className="shrink-0">
          <h1
            className={`custom-heading mb-4 ${headingSize}`}
            data-testid="label-header"
          >
            {heading}
          </h1>
          {subheading && (
            <h2 className="text-center text-[20px]">{subheading}</h2>
          )}
        </div>

        <div className="flex grow items-end justify-center pb-10">
          <Form
            method="post"
            className="w-full max-w-md"
            onSubmit={handleSubmit}
          >
            {infoText && (
              <div className="mt-8 mb-12">
                <InfoBox>{infoText}</InfoBox>
              </div>
            )}
            <div className="flex flex-col space-y-4">
              {buttons.map((button, index) => (
                <button
                  key={`button-${index}`}
                  type="submit"
                  className={`w-full cursor-pointer rounded-[20px] border bg-white px-6 py-6 text-center hover:opacity-80 ${buttonTextSize} ${buttonBgColor} ${
                    clickedValue === button.value ||
                    preselectedValue === button.value
                      ? 'border-2 border-gray-400'
                      : 'border-custom-gray'
                  }`}
                  data-testid={`button-option-${index + 1}`}
                  onClick={() => setClickedValue(button.value)}
                >
                  <div className="items-center justify-center">
                    <p className="font-semibold">{button.text}</p>
                    {button.subtext && <p>{button.subtext}</p>}
                  </div>
                </button>
              ))}
            </div>
          </Form>
        </div>
      </div>
    </PageLayout>
  )
}
